'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useTranslations, useLocale } from 'next-intl'

import { Button } from '@/components/ui/button'
import { GlassSurface } from '@/components/ui/glass'

const CONSENT_KEY = 'sb_cookie_consent'

export type ConsentState = 'accepted' | 'declined' | null

function grantConsent() {
  if (typeof window === 'undefined') return
  if (typeof (window as any).gtag !== 'function') {
    // gtag/js hasn't loaded yet — queue it so it fires once the library arrives
    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).dataLayer.push(['consent', 'update', { analytics_storage: 'granted' }])
    return
  }
  ;(window as any).gtag('consent', 'update', { analytics_storage: 'granted' })
}

export function CookieBanner() {
  const t = useTranslations('cookieBanner')
  const locale = useLocale()
  const [consent, setConsent] = useState<ConsentState | 'loading'>('loading')

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentState | null
    setConsent(stored)
    if (stored === 'accepted') {
      grantConsent()
    }
  }, [])

  function accept() {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    setConsent('accepted')
    grantConsent()
  }

  function decline() {
    localStorage.setItem(CONSENT_KEY, 'declined')
    setConsent('declined')
  }

  // Don't render until we've checked localStorage, and hide if already decided
  if (consent !== null) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('ariaLabel')}
      // z-[110]: consent has to win over everything it could otherwise be
      // trapped behind — the chat trigger (z-40), the open chat panel
      // (z-41/51), and especially Hans (z-42/52, or up to z-100 while
      // perched idle on the closed button — see ChatMascot.tsx). A user
      // can't dismiss a banner that's rendering underneath the thing
      // covering it.
      className="fixed bottom-0 left-0 right-0 z-[110] px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:px-6 md:pt-6 md:pb-[calc(1.5rem+env(safe-area-inset-bottom))] animate-in slide-in-from-bottom-5 fade-in duration-500"
    >
      {/* Phase 7f: one glass surface per page, so the blur stays (budget §2).
          Tokenised colours — legible in both themes. */}
      <GlassSurface
        variant="sidebar"
        elevation="floating"
        className="max-w-4xl mx-auto rounded-2xl"
        style={{ borderRadius: '1rem' }}
      >
      <div className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-foreground mb-1">{t('title')}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t('description')}{' '}
            <Link
              href={`/${locale}/cookie-policy`}
              className="text-foreground font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              {t('learnMore')}
            </Link>
          </p>
        </div>
        <div className="flex gap-3 shrink-0 mt-2 md:mt-0">
          <Button variant="outline" onClick={decline} className="px-5">
            {t('decline')}
          </Button>
          <Button variant="default" onClick={accept} className="px-5">
            {t('accept')}
          </Button>
        </div>
      </div>
      </GlassSurface>
    </div>
  )
}
